"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { BookOpen, Compass, MapPin, Sparkles, CircleX as XCircle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
export default function Distinction() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="whatitisandisnot" data-nav="light" className="relative bg-muted py-20 md:py-28 text-foreground">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto mb-14 max-w-2xl text-center md:mb-18">
          <div className="mb-4 inline-flex items-center justify-center">
            <Badge variant="outline" className="border-secondary/40 bg-background/60 px-3 py-1 font-sans text-xs uppercase tracking-widest text-secondary">
              <Compass className="size-3.5" />
              <span>Transparence</span>
            </Badge>
          </div>
          <h2 className="font-serif font-medium italic tracking-tight text-foreground !text-[40px]">Notre guide pour s'installer en Suède</h2>
          <div className="mx-auto mt-4 h-0.5 w-16 bg-secondary/60" />
        </div>

        {/* Content Comparison Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 gap-8 md:grid-cols-2 md:gap-10">
          {/* Block: Ce que ce n'est pas */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="h-full border border-border/80 bg-background shadow-sm transition-[box-shadow,border-color] duration-300 hover:border-secondary/30 hover:shadow-md">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                    <XCircle className="size-5" />
                  </div>
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Point de vue
                    </span>
                    <CardTitle className="font-serif text-2xl font-semibold italic text-foreground">
                      Ce que ce n&apos;est pas
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-base leading-relaxed text-foreground/85">
                <p className="border-l-2 border-border pl-4">Ce n'est pas un guide généraliste sur la Suède. Nous n'avons vécu qu'à un seul endroit, en banlieue nord de Stockholm, et nous ne prétendons pas savoir ce que c'est de vivre à Malmö ou à Umeå.</p>
                <p className="border-l-2 border-border pl-4">Ce n'est pas non plus un manifeste pour vous convaincre de partir, ni la Suède carte postale des petits chalets rouges au bord des lacs.</p>
                <div className="pt-2">
                  <div className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground">
                    <MapPin className="size-3.5 text-secondary" />
                    <span>Expérience vécue • Stockholm</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Block: Ce que c'est */}
          <motion.div variants={itemVariants} className="h-full">
            <Card className="relative h-full border border-secondary/30 bg-background shadow-sm transition-[box-shadow,border-color] duration-300 hover:border-secondary/60 hover:shadow-md">
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-full bg-secondary/15 text-secondary">
                    <BookOpen className="size-5" />
                  </div>
                  <div>
                    <span className="font-mono text-xs uppercase tracking-wider text-secondary">
                      Démarche
                    </span>
                    <CardTitle className="font-serif text-2xl font-semibold italic text-foreground">
                      Ce que c&apos;est
                    </CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 text-base leading-relaxed text-foreground/85">
                <p className="border-l-2 border-secondary/50 pl-4">C'est un carnet de bord chronologique : le parcours réel que nous avons vécu, découpé en deux grandes étapes : avant le départ (en France) et après (en Suède), jusqu'à ce que notre installation se stabilise.</p>
                <div className="mt-6 rounded-md bg-secondary/10 p-4">
                  <div className="flex items-center gap-2 font-mono text-xs font-medium text-secondary">
                    <Sparkles className="size-3.5" />
                    <span>2 grandes étapes</span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-foreground/80">
                    <div className="rounded bg-background/80 px-2.5 py-1.5 text-center font-medium">
                      1. Avant (en France)
                    </div>
                    <div className="rounded bg-background/80 px-2.5 py-1.5 text-center font-medium">
                      2. Après (en Suède)
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}